import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useAppStore } from '../stores/appStore'
import { homePathForUser, setLoginSurface } from '../utils/loginSurface'
import { takeReturnTo } from '../utils/authTabShare'
import { Eye, EyeOff, LogIn, Loader2 } from 'lucide-react'

export default function LoginPage() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const setActiveCompany = useAppStore((s) => s.setActiveCompany)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const result = await login(username, password, 'internal')
      if (result.success) {
        // İç kapıdan girildi — oturum düşünce buraya dönülsün.
        if (result.user?.role !== 'PARTNER') setLoginSurface(null)
        // Kullanıcının izinli firmasına otomatik geç
        const allowed = result.user?.allowed_companies || ['01', '02']
        const currentActive = sessionStorage.getItem('app_activeCompany') || '01'
        if (!allowed.includes(currentActive) && currentActive !== '*') {
          setActiveCompany(allowed[0] || '01')
        }
        // Yönetici eliyle açılan hesapta ilk giriş → şifre değiştirme ekranı.
        // Şifre yöneticinin belirlediği, kişiye elden verilmiş bir değerdir;
        // kalıcı olmamalı. Bayrak şifre değişince sunucuda temizlenir
        // (auth_service.change_password).
        if (result.user?.must_change_password) {
          navigate('/settings/security?ilk-giris=1')
          return
        }
        // Hedef TEK kaynaktan (utils/loginSurface) — iş ortağı giriş ekranı ve
        // App.jsx'teki PartnerGuard aynı fonksiyonu kullanır. Burada ayrı
        // hesaplandığı sürece partner artık var olmayan '/partner/stock'a
        // gönderiliyordu (panel ana sayfası eklenince bayatladı).
        // Oturumsuz açılan bağlantıdan gelindiyse (ör. mail'deki "VERTEX'te aç") oraya dön
        const back = takeReturnTo()
        navigate(back && result.user?.role !== 'PARTNER' ? back : homePathForUser(result.user))
      } else {
        setError(result.error || 'Giriş başarısız')
      }
    } catch (err) {
      setError('Bir hata oluştu. Lütfen tekrar deneyiniz.')
      console.error('Login error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left — Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#181B34] items-center justify-center relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-[#6161FF]/10 rounded-full" />
        <div className="absolute -bottom-32 -right-16 w-96 h-96 bg-[#6161FF]/5 rounded-full" />
        <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-[#6161FF]/8 rounded-full" />

        <div className="relative z-10 text-center px-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[#6161FF] text-white font-bold text-3xl mb-6 shadow-lg">
            V
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">VERTEX</h1>
          <p className="text-lg text-white/60">ERP Raporlama & CRM Platformu</p>
          <div className="mt-10 flex items-center justify-center gap-6 text-white/40 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-white/80">30+</div>
              <div>Rapor</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white/80">7</div>
              <div>Modül</div>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-bold text-white/80">2</div>
              <div>Firma</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right — Login Form */}
      <div className="flex-1 flex items-center justify-center bg-surface-secondary p-6">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#6161FF] text-white font-bold text-xl mb-3">
              V
            </div>
            <h1 className="text-2xl font-bold text-content-primary">VERTEX</h1>
          </div>

          <div className="bg-white rounded-card shadow-card p-8">
            <h2 className="text-xl font-semibold text-content-primary mb-1">Hoş Geldiniz</h2>
            <p className="text-sm text-content-secondary mb-6">Hesabınıza giriş yapın</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-status-stuck-light border border-status-stuck/20 rounded-input">
                  <p className="text-sm text-status-stuck">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-content-primary mb-1.5">
                  Kullanıcı Adı
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Kullanıcı adınızı girin"
                  className="w-full px-3 py-2.5 border border-surface-border rounded-input text-sm text-content-primary placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400 transition-all"
                  disabled={isLoading}
                  autoComplete="username"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content-primary mb-1.5">
                  Şifre
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Şifrenizi girin"
                    className="w-full px-3 py-2.5 pr-10 border border-surface-border rounded-input text-sm text-content-primary placeholder:text-content-tertiary focus:outline-none focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400 transition-all"
                    disabled={isLoading}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-content-tertiary hover:text-content-secondary transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#6161FF] text-white font-medium text-sm rounded-button hover:bg-[#6161FF]-hover active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Giriş yapılıyor...
                  </>
                ) : (
                  <>
                    <LogIn size={16} />
                    Giriş Yap
                  </>
                )}
              </button>
            </form>

            {/* Açık kayıt kapalıysa /register ekranı zaten "kayıt kapalı" der —
                bağlantıyı gizlemek için ekstra bir public istek atmaya değmez. */}
            <p className="text-center text-sm text-content-secondary mt-5">
              Hesabınız yok mu?{' '}
              <Link to="/register" className="text-[#6161FF] font-medium hover:underline">
                Kayıt olun
              </Link>
            </p>
          </div>

          <p className="text-center text-xs text-content-tertiary mt-6">
            © 2026 VERTEX — Adeptek Bilişim
          </p>
        </div>
      </div>
    </div>
  )
}
